import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  Optional,
  Self
} from '@angular/core';
import { NgModel } from '@angular/forms';

import { SessionService } from '../../sessions/session.service';

@Directive({
  selector: '[currency]'
})
export class CurrencyInputDirective implements OnInit {
  @Input() min  : number;
  @Input() max  : number;
  @Input() round: number;

  private rounding      : number;
  private minValue      : number;
  private maxValue      : number;
  private numberRegExp  : RegExp;

  constructor (
    private el            : ElementRef<HTMLInputElement>,
    private sessionService: SessionService,
    @Optional() @Self()
    private ngModel       : NgModel
  ) {}

  ngOnInit () : void {
    this.minValue = parseFloat(String(this.min)) || 0;
    this.maxValue = parseFloat(String(this.max));
    this.rounding = parseInt(String(this.round), 10) || 2;
  }

  @HostListener('blur')
  onBlur () : void {
    if ( !this.ngModel ) return;

    const value = this.ngModel.value;

    if ( value ) {

      this.el.nativeElement.value = this.maskValue(value);

    }
  }

  @HostListener('input', [ '$event.target.value' ])
  onInput ( viewValue : string ) : void {
    if ( !this.ngModel ) return;

    const settings = this.getSettings();
    let value      = viewValue || '';

    value = value.replace(/[\.,]/g, ( m ) => {
      return m === '.' ? settings.decimal_mark : settings.thousands_separator;
    });

    value = value.replace(/,/g, '');

    if ( value === '.' ) {
      this.ngModel.control.setValue(0, { emitEvent: false });

      return;
    }

    if ( value === '-' ) {
      return;
    }

    if ( this.isEmpty(value) || settings.NUMBER_REGEXP.test(value) ) {
      let parsed = this.isEmpty(value)
        ? 0
        : settings.no_currency_fractions
          ? parseInt(value, 10)
          : parseFloat(value);

      if ( !this.isEmpty(parsed) && !isNaN(this.minValue) && parsed < this.minValue ) {
        this.ngModel.control.setErrors({ min: true });

        return;
      }

      if ( !this.isEmpty(parsed) && !isNaN(this.maxValue) && parsed > this.maxValue ) {
        this.ngModel.control.setErrors({ max: true });

        return;
      }

      parsed = parsed ? this.roundValue(parsed, this.rounding) : 0;

      this.ngModel.control.setErrors(null);
      this.ngModel.viewToModelUpdate(parsed);

      return;
    }

    this.ngModel.control.setErrors({ number: true });
  }

  private getSettings () : any {
    const settings = this.sessionService.currency();

    settings.NUMBER_REGEXP = new RegExp(settings.validation);

    return settings;
  }

  private isEmpty ( value : any ) : boolean {
    return value === undefined || value === '' || value === null || value !== value;
  }

  private maskValue ( value : any ) : string {
    const settings = this.getSettings();
    const x        = value.toString().split('.');
    const rgx      = /(\d+)(\d{3})/;

    while ( rgx.test(x[0]) ) {
      x[0] = x[0].replace(rgx, '$1' + settings.thousands_separator + '$2');
    }

    if ( !x[1] || x[1] === '0' || x[1] === '00' || (x[1] === 0 && this.round) ) {
      x[1] = '';
    }
    else if ( !x[1] || x[1] === '' ) {
      x[1] = '00';
    }
    else if ( x[1].length === 1 ) {
      x[1] = x[1] + '0';
    }
    else if ( settings.no_currency_fractions ) {
      x[1] = '';
    }

    return x[0] + (x[1] ? settings.decimal_mark + x[1] : '');
  }

  private roundValue ( num : number, pow : number ) : number {
    return Math.round(num * (Math.pow(10, pow))) / Math.pow(10, pow);
  }
}
